import React, { ReactNode, useMemo } from "react";
import { FormattedMessage } from "react-intl";
import { defineMessages } from "react-intl";
import { IncidentTimeline } from "./IncidentTimeline";
import { getGroupedPastIncidents } from "@pages/status-page/helpers/pastIncidents";
import { useAllIncidentsData } from "@pages/status-page/hooks/useAllIncidentsData";
import { Label } from "@noones/ui";
import { IncidentStatusEnum } from "@store/types";
import { tw } from "@noones/ui/tw";
import { formatDateUTC } from "@pages/status-page/helpers/formatDate";

export type IncidentUpdatesProps = {
    body: string;
    created_at: string;
    display_at: string;
    id: string;
    incident_id: string;
    status: string;
    updated_at: string;
};

export type IncidentProps = {
    incident_updates?: Array<IncidentUpdatesProps>;
};

type IncidentObjectType = {
    [key: string]: {
        title: ReactNode;
        dot: ReactNode;
    };
};

export const Incident = ({ incident_updates }: IncidentProps) => {
    const incidentTypes: IncidentObjectType = {
        [IncidentStatusEnum.IDENTIFIED]: {
            title: <FormattedMessage {...messages.titleIdentified} />,
            dot: <div className={tw.rounded_full.bg_grey.h_2.w_2} />,
        },
        [IncidentStatusEnum.INVESTIGATING]: {
            title: <FormattedMessage {...messages.titleInvestigating} />,
            dot: <div className={tw.rounded_full.bg_orange.h_2.w_2} />,
        },
        [IncidentStatusEnum.MONITORING]: {
            title: <FormattedMessage {...messages.titleMonitoring} />,
            dot: <div className={tw.rounded_full.bg_dark_blue.h_2.w_2} />,
        },
        [IncidentStatusEnum.RESOLVED]: {
            title: <FormattedMessage {...messages.titleResolved} />,
            dot: <div className={tw.rounded_full.bg_green.h_2.w_2} />,
        },
    };

    return (
        <div className={tw.flex.flex_col.bg_white}>
            {incident_updates?.map((incident, i) => {
                const isLast = i === incident_updates.length - 1;

                return (
                    <div key={i} className={tw.flex.gap_3}>
                        <div className={tw.flex.flex_col.items_center}>
                            <div className={tw.mt_1}>{incidentTypes[incident.status].dot}</div>

                            {!isLast && <div className={tw.flex_1.w_px.bg_light_grey.mt_1} />}
                        </div>

                        <div className={tw.pb_4}>
                            <div className={tw.flex.gap_2.items_center}>
                                <Label size={18} mode="light-green">
                                    {incidentTypes[incident.status].title}
                                </Label>
                            </div>

                            <div className={tw.text_body_14.font_normal.text_black}>
                                {incident.body}
                            </div>

                            <div className={tw.text_body_12.font_normal.text_grey_green}>
                                {formatDateUTC(incident.created_at)}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export const Incidents = () => {
    const { data: incidents } = useAllIncidentsData();

    const groupedIncidents = useMemo(
        () => !!incidents && getGroupedPastIncidents(incidents),
        [incidents],
    );

    if (!incidents) {
        return null;
    }
    return <IncidentTimeline incidents={incidents} groupedIncidents={groupedIncidents} />;
};

const messages = defineMessages({
    titleIdentified: {
        id: "Incident.identified.title",
        defaultMessage: "Identified",
        description: "Identified title",
    },
    titleInvestigating: {
        id: "Incident.investigating.title",
        defaultMessage: "Investigating",
        description: "Investigating title",
    },
    titleMonitoring: {
        id: "Incident.monitoring.title",
        defaultMessage: "Monitoring",
        description: "Monitoring title",
    },
    titleResolved: {
        id: "Incident.resolved.title",
        defaultMessage: "Resolved",
        description: "Resolved title",
    },
});
